import type { BookableAssetDto } from "@/api/types";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { EntityCard } from "@/components/EntityCard/EntityCard";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { UsersCheckIcon } from "@/components/Icons/UsersCheckIcon";
import { useShareEntityMenuItems } from "@/components/ShareButton/helpers";
import { ShareToCommunityFeedModal } from "@/components/ShareToCommunityFeedModal/ShareToCommunityFeedModal";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { getIsBookableAssetSharable } from "@/helpers/bookings";
import { canViewSchedule } from "@/helpers/permissions";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { ShareToChatModal } from "../ShareToChatModal/ShareToChatModal";

interface BookableAssetCardProps {
  asset: BookableAssetDto;
  "data-testid"?: string;
  onEdit: () => void;
  onDelete: () => void;
}
export function BookableAssetCard({
  asset,
  "data-testid": dataTestId,
  onEdit,
  onDelete,
}: BookableAssetCardProps): React.ReactNode {
  const clockDate = useCurrentDate();
  const [isShareToChatModalOpened, shareToChatModalHandler] = useBool(false);
  const [isShareToCommunityFeedModalOpened, shareToCommunityFeedModalHandler] = useBool(false);

  const slug = useSlug();
  const { i18n, t } = useTranslation();
  const sessionUser = useSessionUser();
  const hasPermission = usePermission();
  const shareEntityMenuItems = useShareEntityMenuItems({
    deepLinkPayload: {
      entityType: "bookableAsset",
      entityId: asset.id,
    },
    callbacks: {
      shareToCommunityFeed: shareToCommunityFeedModalHandler.setTrue,
      shareToChat: shareToChatModalHandler.setTrue,
    },
  });

  const assetDetailPath = routes.bookings.assetDetail({
    slug,
    id: asset.id,
  });
  const bookAssetPath = routes.bookings.bookAsset({
    slug,
    aid: asset.id,
  });
  const currentDate = clockDate;
  const publishDate = asset.publishAt ? new Date(asset.publishAt) : undefined;
  const availableFromDate = asset.availableFrom ? new Date(asset.availableFrom) : undefined;
  let scheduledDate: Date | undefined;
  if (availableFromDate && availableFromDate > currentDate) {
    scheduledDate = availableFromDate;
  } else if (publishDate && publishDate > currentDate) {
    scheduledDate = publishDate;
  }
  const unpublishDate = asset.unpublishAt ? new Date(asset.unpublishAt) : undefined;
  const menuActions: ContextMenuItem[] = [
    {
      labels: {
        default: t("common.action.edit"),
      },
      value: "edit",
      icon: <Edit05Icon />,
      "data-testid": "context-menu-edit-btn",
      type: "button",
      isHidden: !asset.canEdit,
      callbackFn: onEdit,
    },
    {
      labels: {
        default: t("common.action.delete"),
      },
      value: "delete",
      icon: <Trash02Icon />,
      "data-testid": "context-menu-delete-btn",
      type: "button",
      isHidden: !asset.canDelete,
      callbackFn: onDelete,
    },
    ...(getIsBookableAssetSharable(asset) ? shareEntityMenuItems : []),
  ];

  return (
    <>
      <EntityCard
        data-testid={dataTestId}
        href={hasPermission(canViewSchedule) ? assetDetailPath : bookAssetPath}
        title={asset.name}
        subtitle={asset.description}
        bannerImg={asset.images[0]}
        menuActions={menuActions}
        renderBadges={() => (
          <>
            {scheduledDate && (
              <Badge variant="warning">
                {t("page.bookings.list-assets.asset.publish-date", {
                  date: formatDate(i18n, "datetimeShort", scheduledDate),
                })}
              </Badge>
            )}
            {unpublishDate && (
              <Badge variant="warning">
                {unpublishDate > clockDate
                  ? t("page.bookings.list-assets.asset.unpublish-date", {
                      date: formatDate(i18n, "datetimeShort", unpublishDate),
                    })
                  : t("page.bookings.list-assets.asset.unpublished")}
              </Badge>
            )}
            {asset.hasAudience && sessionUser.isAdmin && <Badge icon={<UsersCheckIcon />} variant="default" />}
          </>
        )}
        renderCtaAction={() => (
          <Button type="link" size="sm" data-testid="book-asset-btn" href={bookAssetPath} variant="filled">
            {t("page.bookings.list-assets.asset.book")}
          </Button>
        )}
      />
      <ShareToCommunityFeedModal
        isOpened={isShareToCommunityFeedModalOpened}
        onOpenChange={shareToCommunityFeedModalHandler.set}
        entityId={asset.id}
        entityType="bookableAsset"
      />
      <ShareToChatModal
        isOpened={isShareToChatModalOpened}
        onOpenChange={shareToChatModalHandler.set}
        entityType="bookableAsset"
        entityId={asset.id}
      />
    </>
  );
}
